/**
 * MapTapCallout.tsx
 * على الموبايل: النقر على الخريطة لا يفتح لوحة الطقس مباشرةً (تجربة Windy) — بل يُظهر
 * فقاعة قيمة صغيرة فوق نقطة النقر (بنفس أسلوب HoverValueTooltip) مع سهم صغير أسفلها؛
 * الضغط على السهم هو ما يفتح WeatherInfoPanel. هذا يمنح المستخدم معاينة سريعة بلا حجب
 * الخريطة بلوحة كاملة عند كل لمسة.
 */

import { useEffect, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { useMapRef } from './MapContext';
import { useWeatherStore } from '../../store/weatherStore';
import { rasterSampler } from '../../services/rasterSampler';
import {
    formatMainValue, formatPrecipAccumulated, precipitationNote, windDirToCardinal,
    PRECIP_ACCUMULATION_HOURS, isSnowAtTemp,
} from './hoverValueFormat';
import { useTranslation } from '../../i18n/LanguageContext';
import type { ForecastGridType } from '../../config/weatherLayers';

interface MapTapCalloutProps {
    location: { lat: number; lon: number };
    type: ForecastGridType | null;
    timeIndex: number;
    onExpand: () => void;
}

export function MapTapCallout({ location, type, timeIndex, onExpand }: MapTapCalloutProps) {
    const { t } = useTranslation();
    const mapRef = useMapRef();
    const fahrenheit = useWeatherStore((s) => s.units.temperature === 'fahrenheit');
    const windUnit = useWeatherStore((s) => s.units.wind);
    const precipUnit = useWeatherStore((s) => s.units.precipitation);
    const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
    /**
     * آخر قراءة صالحة.
     *
     * كانت الفقاعة تُرجع null كلّما جاءت العيّنة غير جاهزة، فتختفي ثم تعود. ويحدث ذلك
     * عند **سحب** الشريط الزمني تحديداً لا عند تشغيله: التشغيل التلقائي لا يتقدّم إلى
     * ساعة قبل جهوز نسيجها، أمّا السحب فيقفز إليها فوراً. فنُبقي آخر قراءة معروضة حتى
     * تصل قيمة الساعة الجديدة — قيمة متأخّرة جزءاً من الثانية أصدق للعين من وميض.
     */
    const lastReadingRef = useRef<{ main: string; sub?: string; note: string | null } | null>(null);

    // نقطة أو طبقة جديدة ⇒ لا معنى لإبقاء قراءة النقطة السابقة معروضة.
    useEffect(() => { lastReadingRef.current = null; }, [location.lat, location.lon, type]);

    // نُسقط الإحداثية إلى بكسل الشاشة، ونعيد الإسقاط عند تحريك/تكبير الخريطة كي تبقى
    // الفقاعة مثبّتة فوق النقطة نفسها بالضبط.
    useEffect(() => {
        const wrap = mapRef.current;
        if (!wrap) return;
        const map = wrap.getMap();

        const update = () => {
            const point = map.project([location.lon, location.lat]);
            const rect = map.getContainer().getBoundingClientRect();
            setPos({ x: rect.left + point.x, y: rect.top + point.y });
        };
        update();
        map.on('move', update);
        return () => { map.off('move', update); };
    }, [mapRef, location.lat, location.lon]);

    // تحميل مسبق لنسيج الساعة المطلوبة: سحب الشريط يقفز إلى أي ساعة فوراً بلا انتظار
    // جهوزها (بخلاف التشغيل التلقائي الذي يتباطّأ حتى يصل الإطار — راجع canAdvance في
    // useAutoplay)، فبلا هذا تنتظر الفقاعة جالباً آخر ليطلب النسيج صدفةً. وراستر الحرارة
    // معه في طبقة الأمطار: لازم لتمييز الثلج عن المطر في التسمية.
    useEffect(() => {
        if (type) rasterSampler.prefetch(type, timeIndex);
        if (type === 'precipitation') rasterSampler.prefetch('temperature', timeIndex);
    }, [type, timeIndex]);

    if (!pos || !type) return null;

    // الأمطار: الرقم الرئيسي ولون الخريطة كلاهما تراكم 3 ساعات (أسلوب Windy)، وتصنيف
    // الشدّة (note) يُبنى على نفس التراكم أيضاً كي يطابق اللون المرسوم فعلياً. راجع
    // نفس المنطق في HoverValueTooltip.buildCenterState.
    let reading: { main: string; sub?: string; note: string | null } | null = null;
    if (type === 'precipitation') {
        const accumulated = rasterSampler.sampleAccumulated(type, timeIndex, location.lat, location.lon, PRECIP_ACCUMULATION_HOURS);
        if (accumulated !== null && Number.isFinite(accumulated)) {
            // نفس منطق HoverValueTooltip: حرارة النقطة تحدّد إن كان الهطول ثلجاً أم مطراً.
            const snow = isSnowAtTemp(rasterSampler.sampleScalar('temperature', timeIndex, location.lat, location.lon));
            const formatted = formatPrecipAccumulated(accumulated, t, precipUnit, snow);
            reading = { main: formatted.main, sub: formatted.sub, note: precipitationNote(type, accumulated, t, snow) };
        }
    } else {
        const value = rasterSampler.sampleScalar(type, timeIndex, location.lat, location.lon);
        if (value !== null && Number.isFinite(value)) {
            reading = {
                main: formatMainValue(type, value, fahrenheit, t, windUnit, precipUnit),
                note: precipitationNote(type, value, t),
            };
        }
    }
    // العيّنة غير جاهزة (ساعة سُحب إليها للتوّ) ⇒ نُبقي آخر قراءة بدل إخفاء الفقاعة.
    if (reading) lastReadingRef.current = reading;
    else reading = lastReadingRef.current;
    if (!reading) return null;
    const { main, sub, note } = reading;

    // سطر اتجاه الرياح مفيد فقط في طبقة الرياح نفسها؛ باقي الطبقات (حرارة، ضغط،
    // أمطار، هبّات...) تعرض قيمتها الخاصة فقط بلا سياق رياح مقحَم — أسلوب Zoom Earth.
    const wp = type === 'wind' ? rasterSampler.sampleWind(timeIndex, location.lat, location.lon) : null;
    const wind = wp && wp.speed >= 0.5
        ? { speed: wp.speed, dir: wp.direction, cardinal: windDirToCardinal(wp.direction, t) }
        : null;

    return (
        <div className="map-tap-callout fixed z-[1200] translate-x-[-50%] translate-y-[calc(-100%-12px)] flex flex-col items-center pointer-events-none animate-map-tap-callout-in [animation-duration:0.16s]" style={{ left: pos.x, top: pos.y }}>
            <div className="flex items-center gap-2 px-3 py-[5px] rounded-full bg-[rgba(22,27,34,0.94)] light:bg-[rgba(255,255,255,0.96)] text-[#f3f6fa] light:text-[#16202c] text-[13px] font-bold leading-[1.4] whitespace-nowrap border border-white/[0.16] light:border-black/[0.12] shadow-[0_4px_16px_rgba(0,0,0,0.4)] light:shadow-[0_4px_16px_rgba(0,0,0,0.22)] [direction:ltr] [&_.hv-main]:text-sm [&_.hv-main]:font-extrabold [&_.hv-note]:text-xs [&_.hv-note]:font-semibold [&_.hv-note]:opacity-[0.82] [&_.hv-note+.hv-main]:ps-2 [&_.hv-note+.hv-main]:border-s [&_.hv-note+.hv-main]:border-white/[0.22] [&_.hv-sub]:text-[11px] [&_.hv-sub]:font-medium [&_.hv-sub]:opacity-65 [&_.hv-sub]:whitespace-nowrap [&_.hv-wind]:inline-flex [&_.hv-wind]:items-center [&_.hv-wind]:gap-[5px] [&_.hv-wind]:text-xs [&_.hv-wind]:font-semibold [&_.hv-wind]:opacity-90 [&_.hv-sep]:w-px [&_.hv-sep]:h-[13px] [&_.hv-sep]:bg-current [&_.hv-sep]:opacity-[0.28] [&_.hv-arrow]:inline-block [&_.hv-arrow]:text-[13px] [&_.hv-arrow]:leading-none [&_.hv-arrow]:[transform-origin:center]">
                {note && <span className="hv-note">{note}</span>}
                <span className="hv-main">{main}</span>
                {sub && <span className="hv-sub">{sub}</span>}
                {wind && (
                    <span className="hv-wind">
                        <span className="hv-sep" />
                        <span className="hv-arrow" style={{ transform: `rotate(${wind.dir + 180}deg)` }}>↑</span>
                        {wind.cardinal}
                    </span>
                )}
            </div>
            <button
                className="pointer-events-auto -mt-px w-[30px] h-5 border border-white/[0.16] light:border-black/[0.12] border-t-0 rounded-b-xl rounded-t-none bg-[rgba(22,27,34,0.94)] light:bg-[rgba(255,255,255,0.96)] text-[#f3f6fa] light:text-[#16202c] flex items-center justify-center text-[13px] cursor-pointer shadow-[0_4px_16px_rgba(0,0,0,0.4)] light:shadow-[0_4px_16px_rgba(0,0,0,0.22)] active:bg-[rgba(43,132,255,0.9)]"
                onClick={onExpand}
                type="button"
                aria-label={t('weather.openDetails')}
            >
                <FiChevronDown />
            </button>
        </div>
    );
}
